import { useTranslation } from "react-i18next"
import { Heading, Text } from "@/components/ui/typography"
import LiquidGlassCard from "@/components/landing/LiquidGlassCard"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { sfIcons } from '@/lib/sf-icons'

type SoloFounderSectionProps = {
  id?: string
}

// Icon mapping for trust metrics
const iconMap: Record<string, typeof sfIcons.security> = {
  shield: sfIcons.security,
  lock: sfIcons.lock,
  "file-check": sfIcons.fileCheck,
  "clipboard-check": sfIcons.clipboardCheck,
} as const

export function SoloFounderSection({ id }: SoloFounderSectionProps) {
  const { t } = useTranslation("marketing")
  
  // Trust metrics from SSOT (marketing.json)
  const trustMetrics = ((t("soloFounder.trustMetrics", { returnObjects: true }) as any[]) || []).map((metric: any) => ({
    title: metric.title,
    description: metric.description,
    icon: iconMap[metric.icon] || sfIcons.security,
  }))
  return (
    <section
      id={id}
      className={cn(
        "relative py-16 sm:py-24 overflow-hidden",
        "bg-slate-50/50 dark:bg-slate-950/50"
      )}
      aria-labelledby="solo-founder-title"
    >
      {/* Color Lab: Subtle Blue-Lavender gradient */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse at 0% 50%, hsla(217, 91%, 60%, 0.08) 0%, transparent 45%),
            radial-gradient(ellipse at 100% 100%, hsla(270, 95%, 75%, 0.10) 0%, transparent 45%)
          `
        }}
      />
      <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
        <div className="grid gap-8 sm:gap-10 md:gap-12 lg:gap-16 lg:grid-cols-5 items-start">

 {/* Left: Story (3/5 = ~60% golden ratio major) */}
          <div className="lg:col-span-3 space-y-6 sm:space-y-8 md:space-y-10">
            <div className="space-y-4 sm:space-y-5 md:space-y-6">
              <Text variant="caption" color="muted" className="uppercase tracking-widest">
                {t("soloFounder.badge")}
              </Text>
              <Heading level={2} id="solo-founder-title" className="leading-tight">
                {t("soloFounder.headline")}
              </Heading>
              <Text variant="lg" color="muted" className="leading-relaxed">
                {t("soloFounder.description")}
              </Text>
            </div>

            <div className="space-y-4 sm:space-y-5 md:space-y-6">
              {((t("soloFounder.principles", { returnObjects: true }) as any[]) || []).map((item: any, i: number) => (
                <div key={i} className="flex gap-3 sm:gap-4">
                  {/* Canon v1.4.5: Zen State for trust metrics */}
                  <div className={cn(
                    "mt-1 flex h-6 w-6 items-center justify-center rounded-full font-bold shrink-0",
                    semanticTokens.typography.captionSmall,
                    semanticTokens.zen.bg,
                    semanticTokens.zen.fg
                  )}>
                    {i + 1}
                  </div>
                  <div className="min-w-0">
                    <Heading level={4}>{item.title}</Heading>
                    <Text variant="sm" color="muted" className="leading-relaxed">{item.description}</Text>
                  </div>
                </div>
              ))}
            </div>

            <Text variant="sm" color="default" className="font-medium pt-2">
              {t("soloFounder.closing")}
            </Text>
          </div>

 {/* Right: Trust Metrics (2/5 = ~40% golden ratio minor) */}
          <div className="lg:col-span-2 grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-1">
            {trustMetrics.map((metric, idx) => {
              const Icon = metric.icon || sfIcons.security
              if (!Icon) return null
              return (
                <LiquidGlassCard
                  key={idx}
                  tier="surface"
                  interactive
                  className="flex flex-col"
                >
                  <div className="p-5 sm:p-6">
                    <div className="flex items-start gap-3 sm:gap-4">
                      {/* Canon v1.4.5: Zen State for trust icon */}
                      <div className={cn(
                        "inline-flex items-center justify-center rounded-xl transition-colors",
                        "border-2",
                        "h-10 w-10 sm:h-11 sm:w-11",
                        semanticTokens.zen.border,
                        semanticTokens.zen.fg,
                        "group-hover:border-border/80"
                      )}>
                        <Icon className="h-5 w-5" aria-hidden="true" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <Heading level={4}>
                          {metric.title}
                        </Heading>
                        <Text variant="caption" color="muted" className="mt-1">
                          {metric.description}
                        </Text>
                      </div>
                    </div>
                  </div>
                </LiquidGlassCard>
              )
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <LiquidGlassCard
          tier="surface"
          className="mt-12 sm:mt-16 text-center"
        >
          <div className="p-8 sm:p-10 md:p-12">
            <Heading level={3} className="mb-3 sm:mb-4">
              {t("soloFounder.cta.headline")}
            </Heading>
            <Text variant="lg" color="muted" className="max-w-2xl mx-auto leading-relaxed">
              {t("soloFounder.cta.description")}
            </Text>
          </div>
        </LiquidGlassCard>
      </div>
    </section>
  )
}
